import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { compare } from 'compare-versions';

import { semanticVersion } from 'external_data';
import { ThunkAction } from 'reducers';
import { getClientCode, getSessionKey } from 'reducers/Login';
import { getMSEndpointFromLS } from 'reducers/installer';
import { getMsInfo } from 'actions/integrations/printer/utils';

import { Statistics, Statistic } from './Statistics';

dayjs.extend(utc);
dayjs.extend(timezone);

const deviceID = localStorage.getItem('msLogger-device-id') ?? uuidv4();

const METRICS_COLLECTOR_VERSION_SUPPORTING_DIRECT_STAT_SENDING = '0.1.1';

/**
 * If printing takes longer than the defined value, it should be considered slow and a log collection should be sent
 */
export const LONG_PRINTING_PERIOD = 5;
class PrintingStatistics extends Statistics {
  getStatsToSend(): Omit<Statistic, 'timestamp'>[] {
    const totalStat = this.getFullDurationStat();

    // If at the moment of sending the Total log is not yet finished, finish it
    if (totalStat && totalStat?.record?.duration === undefined) {
      this.finishLog(totalStat.description);
    }
    return this.getAllStatistics();
  }

  /**
   * @deprecated Unused in core code, but can be used for testing purposes to not pollute logs in cloud
   */
  sendLogToLogger(clientCode?: string): void {
    const entry = this.getEntry();

    if (!entry.statistics.length) {
      return;
    }

    const statsToSend = this.getStatsToSend();

    window
      .fetch('https://localhost.erply.com:10900/log', {
        method: 'POST',
        body: JSON.stringify({
          loglevel: 'ERROR',
          entry: statsToSend,
          meta: {
            filename: 'printingMeasurements',
            application: {
              name: 'POS Brazil',
              endpoint: window.location.href,
              version: semanticVersion,
            },
            clientCode,
            deviceID,
          },
        }),
      })
      .then(() => {
        // Clear the cached logs as soon as network request finished
        this.clearAll();
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.error(
          'Failed to send printing measurement log to Logger MS',
          e,
        );
      });
  }

  sendToMetricsCollector(): ThunkAction<void> {
    return async (dispatch, getState) => {
      const entry = this.getEntry();

      if (!entry.statistics.length) {
        console.info('Did not send logs to metrics. No logs to send');
        return;
      }

      const metricsMSInfo = await dispatch(getMsInfo('metrics-collector-ms'));
      if (!metricsMSInfo) {
        console.error(
          'Cannot send logs to metrics collector: Failed to get MS info',
        );
        return;
      }
      if (metricsMSInfo.status !== 'StatusRunning') {
        console.error('Cannot send logs to metrics collector: MS not running');
        return;
      }
      // Support for sending a log directly was added in '0.1.1'
      if (
        compare(
          metricsMSInfo.version,
          METRICS_COLLECTOR_VERSION_SUPPORTING_DIRECT_STAT_SENDING,
          '<',
        )
      ) {
        console.error(
          'Cannot send logs to metrics collector: Installed MS version does not support sending statistics directly',
        );
        return;
      }

      const clientCode = getClientCode(getState());
      const sessionKey = getSessionKey(getState());
      const headers = {
        clientCode,
        sessionKey,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };
      const statsToSend = this.getStatsToSend();

      const body = {
        Statistics: {
          items: {
            'POS Brazil': statsToSend,
          },
        },
      };

      const url = `${getMSEndpointFromLS('metrics-collector-ms') ||
        'https://localhost.erply.com:10901/'}api/v1/statistics`;
      window
        .fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.error(
            'Failed to send printing measurement log to Metrics Collector MS',
            e,
          );
        });
    };
  }

  /**
   * Use this function to finish logging the Total time entry and send to metrics in case the printing took longer than LONG_PRINTING_PERIOD
   * @param description The description of the TOTAL entry - once logged, will send logs to metrics
   * @param error Optional error/info message
   */
  finishLogAndSendToMetrics(
    description: string,
    error?: string,
  ): ThunkAction<void> {
    return async dispatch => {
      // Finish logging the final action
      this.finishLog(description, error);
      const fullDuration = this.getFullDurationStat()?.record?.duration;
      if (fullDuration && fullDuration > LONG_PRINTING_PERIOD) {
        // Don't await not to block the printing
        dispatch(this.sendToMetricsCollector()).finally(() => {
          // Always clear the logs no matter if they were sent or not
          this.clearAll();
        });
      } else {
        // Manually clear the logs if no message was sent
        this.clearAll();
      }
    };
  }
}

export const printingStatistics = new PrintingStatistics();
